import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useData } from '../../../contexts/DataContext';
import useFormSecurity from '../../../hooks/useFormSecurity';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import EmergencyChecklist from '../components/EmergencyChecklist';

const { FiArrowLeft, FiArrowRight, FiSend, FiAlertTriangle, FiUsers, FiFileText, FiMapPin, FiHeart, FiShield, FiEye, FiDollarSign, FiHome, FiLightbulb, FiPrinter, FiCheckCircle, FiEyeOff, FiCheck, FiLoader } = FiIcons;

const GuidanceTip = ({ text }) => (
  <div className="flex items-start gap-2 text-sm text-blue-700 bg-blue-50 p-3 rounded-md mb-2 border border-blue-100">
    <SafeIcon icon={FiLightbulb} className="mt-0.5 flex-shrink-0 text-blue-500" />
    <span className="text-xs leading-relaxed">{text}</span>
  </div>
);

const RUHForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addReport, companies } = useData();
  const { validateSubmission, HoneypotField } = useFormSecurity('ruh_form');
  
  const [currentStep, setCurrentStep] = useState(1);
  const [showEmergencyChecklist, setShowEmergencyChecklist] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    companyId: sessionStorage.getItem('hmsCompanyId') || '',
    reporterName: user?.name || '',
    reporterEmail: user?.email || '',
    reporterPhone: '',
    isAnonymous: false,
    reportDate: new Date().toISOString().split('T')[0],
    categories: [],
    incidentDescription: '',
    incidentDate: '',
    incidentLocation: '',
    witnessesPresent: false,
    witnessNames: '',
    additionalInfo: '',
    attachments: [],
    followUpRequested: false,
    preferredContact: 'email'
  });

  const ruhCategories = [
    { id: 'workplace_harassment', label: 'Trakassering på arbeidsplassen', icon: FiUsers, color: 'bg-red-100 border-red-300 text-red-800 hover:bg-red-200', description: 'Mobbing, trakassering eller uønsket oppførsel' },
    { id: 'sexual_harassment', label: 'Seksuell trakassering', icon: FiAlertTriangle, color: 'bg-pink-100 border-pink-300 text-pink-800 hover:bg-pink-200', description: 'Uønskede seksuelle kommentarer eller handlinger' },
    { id: 'discrimination', label: 'Diskriminering', icon: FiUsers, color: 'bg-purple-100 border-purple-300 text-purple-800 hover:bg-purple-200', description: 'Forskjellsbehandling basert på personlige egenskaper' },
    { id: 'bullying', label: 'Mobbing', icon: FiAlertTriangle, color: 'bg-orange-100 border-orange-300 text-orange-800 hover:bg-orange-200', description: 'Systematisk negativ oppførsel mot enkeltpersoner' },
    { id: 'substance_abuse', label: 'Rus på arbeidsplassen', icon: FiAlertTriangle, color: 'bg-yellow-100 border-yellow-300 text-yellow-800 hover:bg-yellow-200', description: 'Bruk av alkohol eller narkotika på jobben' },
    { id: 'violence_threats', label: 'Vold eller trusler', icon: FiShield, color: 'bg-red-100 border-red-300 text-red-800 hover:bg-red-200', description: 'Fysisk vold eller verbale trusler' },
    { id: 'corruption', label: 'Korrupsjon', icon: FiDollarSign, color: 'bg-indigo-100 border-indigo-300 text-indigo-800 hover:bg-indigo-200', description: 'Bestikkelser, underslag eller maktmisbruk' },
    { id: 'fraud', label: 'Bedrageri', icon: FiFileText, color: 'bg-cyan-100 border-cyan-300 text-cyan-800 hover:bg-cyan-200', description: 'Økonomisk bedrageri eller falske opplysninger' },
    { id: 'safety_violations', label: 'Brudd på sikkerhetsrutiner', icon: FiAlertTriangle, color: 'bg-amber-100 border-amber-300 text-amber-800 hover:bg-amber-200', description: 'Ignorering av sikkerhetsprosedyrer' },
    { id: 'environmental_violations', label: 'Miljøbrudd', icon: FiMapPin, color: 'bg-green-100 border-green-300 text-green-800 hover:bg-green-200', description: 'Brudd på miljøregelverk eller -rutiner' },
    { id: 'data_breach', label: 'Brudd på personvern/GDPR', icon: FiEye, color: 'bg-slate-100 border-slate-300 text-slate-800 hover:bg-slate-200', description: 'Ulovlig behandling av personopplysninger' },
    { id: 'ethical_violations', label: 'Brudd på etiske retningslinjer', icon: FiFileText, color: 'bg-violet-100 border-violet-300 text-violet-800 hover:bg-violet-200', description: 'Handlinger som strider mot bedriftens verdier' },
    { id: 'retaliation', label: 'Gjengjeldelse', icon: FiAlertTriangle, color: 'bg-rose-100 border-rose-300 text-rose-800 hover:bg-rose-200', description: 'Negative konsekvenser for varsling' },
    { id: 'other', label: 'Annet', icon: FiFileText, color: 'bg-gray-100 border-gray-300 text-gray-800 hover:bg-gray-200', description: 'Andre uønskede hendelser' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCategoryChange = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(categoryId)
        ? prev.categories.filter(id => id !== categoryId)
        : [...prev.categories, categoryId]
    }));
  };

  const nextStep = () => { if (currentStep < 5) setCurrentStep(currentStep + 1); };
  const prevStep = () => { if (currentStep > 1) setCurrentStep(currentStep - 1); };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validateSubmission()) return;
    setIsSubmitting(true);
    
    try {
      const reportData = {
        ...formData,
        type: 'ruh_report',
        severity: 'medium',
        title: `RUH: ${formData.categories.map(cat => ruhCategories.find(rc => rc.id === cat)?.label).join(', ')}`,
        description: formData.incidentDescription,
        submittedAt: new Date().toISOString()
      };
      
      const newReport = addReport(reportData);
      navigate('/hms/confirmation', { state: { reportId: newReport.id, reportData: newReport } });
    } catch (error) {
      console.error('Error submitting report:', error);
      setIsSubmitting(false);
    }
  };

  const getSelectedCategories = () => {
    return formData.categories.map(catId => ruhCategories.find(cat => cat.id === catId)?.label).filter(Boolean);
  };

  // Preview helper
  const PreviewField = ({ label, value, fullWidth = false }) => (
    <div className={`${fullWidth ? 'md:col-span-2' : ''} border-b border-gray-100 pb-3`}>
      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
      <p className="text-sm text-gray-800 font-medium whitespace-pre-wrap">{value || '-'}</p>
    </div>
  );

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiAlertTriangle} className="text-red-600 text-xl" />
                  <div>
                    <h3 className="font-semibold text-red-900">Trenger du akutt hjelp?</h3>
                    <p className="text-sm text-red-700">Ved akutte situasjoner - ring 113, 110 eller 112 først!</p>
                  </div>
                </div>
                <button type="button" onClick={() => setShowEmergencyChecklist(true)} className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
                  <SafeIcon icon={FiHeart} /> <span>Akutt hjelp</span>
                </button>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Grunnleggende informasjon</h3>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2"> Velg bedrift * </label>
                  <select value={formData.companyId} onChange={(e) => handleInputChange('companyId', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent" required >
                    <option value="">Velg bedrift</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>{company.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2"> Rapportdato </label>
                  <input type="date" value={formData.reportDate} onChange={(e) => handleInputChange('reportDate', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                </div>
                <div className="border-t pt-6">
                  <h4 className="text-md font-semibold text-gray-900 mb-4">Kontaktinformasjon</h4>
                  <div className="mb-4">
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" checked={formData.isAnonymous} onChange={(e) => handleInputChange('isAnonymous', e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                      <span className="text-sm text-gray-700">Jeg ønsker å rapportere anonymt</span>
                    </label>
                  </div>
                  {!formData.isAnonymous && (
                    <div className="grid md:grid-cols-2 gap-6">
                      <div><label className="block text-sm font-medium text-gray-700 mb-2">Navn *</label><input type="text" value={formData.reporterName} onChange={(e) => handleInputChange('reporterName', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" required={!formData.isAnonymous} /></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-2">E-post *</label><input type="email" value={formData.reporterEmail} onChange={(e) => handleInputChange('reporterEmail', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" required={!formData.isAnonymous} /></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Velg kategorier som beskriver hendelsen</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {ruhCategories.map((category) => (
                <motion.div key={category.id} whileHover={{ scale: 1.02 }} className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${formData.categories.includes(category.id) ? 'border-blue-500 bg-blue-50' : `border-gray-200 ${category.color}`}`} onClick={() => handleCategoryChange(category.id)} >
                  <div className="flex items-start space-x-3">
                    <SafeIcon icon={category.icon} className={`text-xl mt-1 ${formData.categories.includes(category.id) ? 'text-blue-600' : 'text-current'}`} />
                    <div className="flex-1">
                      <span className={`font-medium block mb-1 ${formData.categories.includes(category.id) ? 'text-blue-900' : 'text-current'}`}>{category.label}</span>
                      <p className={`text-xs ${formData.categories.includes(category.id) ? 'text-blue-700' : 'text-current opacity-70'}`}>{category.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Beskrivelse av hendelsen</h3>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Detaljert beskrivelse *</label>
                <textarea value={formData.incidentDescription} onChange={(e) => handleInputChange('incidentDescription', e.target.value)} placeholder="Beskriv hva som skjedde..." rows="6" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent" required />
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div><label className="block text-sm font-medium text-gray-700 mb-2">Dato</label><input type="date" value={formData.incidentDate} onChange={(e) => handleInputChange('incidentDate', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-2">Sted</label><input type="text" value={formData.incidentLocation} onChange={(e) => handleInputChange('incidentLocation', e.target.value)} placeholder="F.eks. Kontoret" className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>
              </div>
            </div>
          </div>
        );
      case 4:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Tilleggsinformasjon og oppfølging</h3>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tilleggsinformasjon</label>
                <textarea value={formData.additionalInfo} onChange={(e) => handleInputChange('additionalInfo', e.target.value)} placeholder="Noe annet viktig?" rows="4" className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" checked={formData.followUpRequested} onChange={(e) => handleInputChange('followUpRequested', e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  <span className="text-sm font-medium text-gray-700">Jeg ønsker oppfølging</span>
                </label>
              </div>
            </div>
          </div>
        );
      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Forhåndsvisning av RUH</h2>
              <p className="text-gray-600">Se gjennom hele rapporten før innsending.</p>
            </div>
            <div className="bg-white shadow-2xl rounded-sm border border-gray-200 overflow-hidden max-w-4xl mx-auto flex flex-col font-serif min-h-[600px]">
              <div className="bg-purple-600 h-2 w-full"></div>
              <div className="p-12 space-y-10">
                <div className="flex justify-between items-start border-b-2 border-gray-900 pb-8">
                  <div>
                    <h1 className="text-4xl font-black text-gray-900 tracking-tighter uppercase mb-2">Rapport Uønsket Hendelse (RUH)</h1>
                    <div className="flex items-center gap-4 text-sm text-gray-500 font-sans">
                      <span className="flex items-center gap-1 font-mono uppercase">ID: RUH-PROT-{Date.now().toString().slice(-6)}</span>
                    </div>
                  </div>
                  <div className="text-right font-sans">
                    <div className="w-16 h-16 bg-gray-100 rounded-lg ml-auto mb-2 flex items-center justify-center text-gray-400">
                      <SafeIcon icon={FiFileText} size={32} />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 font-sans">
                  <section className="space-y-6">
                    <h3 className="text-xs font-black text-purple-600 uppercase tracking-[0.2em] border-b border-purple-100 pb-2">1. Kategorisering</h3>
                    <div className="flex flex-wrap gap-2 pt-2">
                      {getSelectedCategories().map((cat, i) => (
                        <span key={i} className="bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-xs font-bold border border-purple-100">{cat}</span>
                      ))}
                    </div>
                  </section>
                  <section className="space-y-6">
                    <h3 className="text-xs font-black text-purple-600 uppercase tracking-[0.2em] border-b border-purple-100 pb-2">2. Tid & Sted</h3>
                    <div className="space-y-4">
                      <PreviewField label="Hendelsesdato" value={formData.incidentDate} />
                      <PreviewField label="Lokasjon" value={formData.incidentLocation} />
                    </div>
                  </section>
                  <section className="md:col-span-2 space-y-6">
                    <h3 className="text-xs font-black text-purple-600 uppercase tracking-[0.2em] border-b border-purple-100 pb-2">3. Hendelsesforløp</h3>
                    <div className="bg-gray-50 p-6 rounded-lg text-gray-700 leading-relaxed italic border border-gray-100">
                      {formData.incidentDescription}
                    </div>
                  </section>
                  <section className="md:col-span-2 space-y-6">
                    <h3 className="text-xs font-black text-purple-600 uppercase tracking-[0.2em] border-b border-purple-100 pb-2">4. Rapportør & Oppfølging</h3>
                    <div className="grid md:grid-cols-2 gap-8">
                      {formData.isAnonymous ? (
                        <div className="flex items-center gap-3 text-purple-600">
                          <SafeIcon icon={FiEyeOff} size={24} />
                          <span className="font-black uppercase tracking-widest text-sm">Anonym Innsending</span>
                        </div>
                      ) : (
                        <>
                          <PreviewField label="Navn" value={formData.reporterName} />
                          <PreviewField label="Kontakt" value={formData.reporterEmail} />
                        </>
                      )}
                      <PreviewField label="Ønsker oppfølging" value={formData.followUpRequested ? 'JA' : 'NEI'} />
                    </div>
                  </section>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <HoneypotField />
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-6 flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold mb-2">Rapporter Uønskede Hendelser (RUH)</h1>
              <p className="text-purple-100">Steg {currentStep} av 5</p>
            </div>
          </div>
          <div className="bg-gray-100 px-6 py-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-purple-600 h-2 rounded-full transition-all duration-300" style={{ width: `${(currentStep / 5) * 100}%` }}></div>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="p-6">
            {renderStep()}
            <div className="flex items-center justify-between pt-6 border-t border-gray-200 mt-8">
              <button type="button" onClick={prevStep} disabled={currentStep === 1 || isSubmitting} className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${currentStep === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}>
                <SafeIcon icon={FiArrowLeft} /> <span>Forrige</span>
              </button>
              <div className="flex gap-3">
                {currentStep === 5 && (
                  <button type="button" onClick={() => window.print()} className="flex items-center space-x-2 px-4 py-2 border-2 border-gray-800 rounded-lg text-gray-800 font-bold">
                    <SafeIcon icon={FiPrinter} /> <span>Print</span>
                  </button>
                )}
                {currentStep < 5 ? (
                  <button type="button" onClick={nextStep} className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg transition-colors">
                    <span>{currentStep === 4 ? 'Se Forhåndsvisning' : 'Neste'}</span> <SafeIcon icon={FiArrowRight} />
                  </button>
                ) : (
                  <button type="submit" disabled={isSubmitting} className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-8 py-2 rounded-lg transition-colors font-bold shadow-lg">
                    {isSubmitting ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiSend} />} <span>Bekreft & Send RUH</span>
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
      {showEmergencyChecklist && <EmergencyChecklist onClose={() => setShowEmergencyChecklist(false)} companyId={formData.companyId} />}
    </div>
  );
};

export default RUHForm;